/**
 * The only module in this app allowed to touch web storage, and the only reason
 * it exists is the PayPal handoff.
 *
 * Paying means leaving this origin and coming back. A signed-in collector
 * returns with the `lp_customer_session` cookie and needs nothing else. A guest
 * has no cookie, so the opaque continuation token core issued in
 * `CheckoutStartResult.checkout_token` has to survive the round trip, or the
 * returning customer cannot ask core what happened to the money they just
 * approved.
 *
 * The rules this module enforces, all of them checked by tests:
 *
 * - **`sessionStorage` only.** Tab-scoped and cleared when the tab closes.
 *   `localStorage` is banned outright by the ESLint config, in every file
 *   including this one's callers.
 * - **Opaque identifiers only.** Item ids, a quote id, an order id, and the
 *   continuation token. No name, no email address, no postal address, no card
 *   detail, no price. `write()` refuses a payload that looks like an email or
 *   carries an unexpected key, so a future edit cannot quietly widen it.
 * - **Never rendered, never logged.** The token is passed to the transport as a
 *   header and nowhere else.
 * - **Bounded.** A hostile or corrupted entry is discarded, not repaired.
 *
 * Everything else a customer types — their address, their email address, their
 * inquiry — lives in React state for the life of the page and is deliberately
 * *not* persisted. Design Bible 9.1 (3.3.7 Redundant Entry) is satisfied for a
 * returning signed-in collector by the address core already holds, not by this
 * browser keeping a copy of one.
 */

const KEY = "lp.storefront.tab";
const MAX_ITEMS = 20;
const MAX_ID = 100;
const MAX_TOKEN = 200;

export interface TabState {
  /** Exact `physical_item_id`s the collector has put in the cart. */
  readonly cart: readonly string[];
  readonly quoteID: string | null;
  readonly orderID: string | null;
  /** Guest continuation capability. Absent for a signed-in collector. */
  readonly checkoutToken: string | null;
}

export const EMPTY_TAB: TabState = { cart: [], quoteID: null, orderID: null, checkoutToken: null };

/** Anything that looks like an email address is refused outright (no PII). */
const EMAIL_SHAPED = /@/;

function validID(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= maximum &&
    !EMAIL_SHAPED.test(value) &&
    /^[A-Za-z0-9._~-]+$/.test(value) &&
    !/[\u0000-\u001f\u007f]/.test(value)
  );
}

function storage(): Storage | null {
  try {
    // Safari in private mode throws on access rather than returning null.
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function read(): TabState {
  const store = storage();
  if (store === null) return EMPTY_TAB;
  let raw: unknown;
  try {
    const text = store.getItem(KEY);
    if (text === null) return EMPTY_TAB;
    raw = JSON.parse(text);
  } catch {
    return EMPTY_TAB;
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return EMPTY_TAB;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!["cart", "quoteID", "orderID", "checkoutToken"].includes(key)) return EMPTY_TAB;
  }
  const cart = Array.isArray(record["cart"]) ? record["cart"] : [];
  if (cart.length > MAX_ITEMS || !cart.every((entry) => validID(entry, MAX_ID))) return EMPTY_TAB;
  const quoteID = record["quoteID"];
  const orderID = record["orderID"];
  const token = record["checkoutToken"];
  return {
    cart: [...new Set(cart as string[])],
    quoteID: validID(quoteID, MAX_ID) ? quoteID : null,
    orderID: validID(orderID, MAX_ID) ? orderID : null,
    checkoutToken: validID(token, MAX_TOKEN) ? token : null
  };
}

export function write(next: TabState): void {
  const store = storage();
  if (store === null) return;
  if (next.cart.length > MAX_ITEMS || !next.cart.every((entry) => validID(entry, MAX_ID))) return;
  if (next.quoteID !== null && !validID(next.quoteID, MAX_ID)) return;
  if (next.orderID !== null && !validID(next.orderID, MAX_ID)) return;
  if (next.checkoutToken !== null && !validID(next.checkoutToken, MAX_TOKEN)) return;
  try {
    store.setItem(KEY, JSON.stringify(next));
  } catch {
    // A full or disabled store is not an error the customer needs to see: the
    // cart still works for this page, and the checkout page re-reads core.
  }
}

export function clear(): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(KEY);
  } catch {
    // Nothing to recover from; the tab is closing or storage is unavailable.
  }
}

/*
 * External-store interface for `useSyncExternalStore`.
 *
 * The cart is genuinely external state that differs between the server and the
 * browser: the server has no idea what is in anyone's cart and must render an
 * empty one, and the browser restores the tab's cart on hydration. React's
 * external-store hook is the mechanism designed for exactly that split —
 * `getServerSnapshot` returns the empty cart, so the hydration render matches
 * the server markup, and the browser snapshot is adopted immediately after.
 *
 * The snapshot is cached because `getSnapshot` is called during render and must
 * return a referentially stable value; parsing storage on every render would
 * return a fresh object each time and loop.
 */
const listeners = new Set<() => void>();
let cached: TabState | null = null;

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): TabState {
  cached ??= read();
  return cached;
}

export function getServerSnapshot(): TabState {
  return EMPTY_TAB;
}

/*
 * Whether the snapshot being rendered is the browser's, not the server's.
 *
 * The hydration render is served `getServerSnapshot`, so it sees an empty tab
 * even when the tab holds a checkout token. Anything that fires *once* on the
 * strength of tab state — the PayPal-return continuation is the only such thing
 * — has to wait for the browser snapshot or it sends a request with no
 * credential. Reading this through the same store, with the same subscription,
 * is what guarantees it flips in the same render that adopts the real tab: a
 * plain client render calls `readySnapshot` immediately, and a hydration render
 * calls `readyServerSnapshot` and then re-renders when React reconciles the
 * store.
 */
export function readySnapshot(): boolean {
  return true;
}

export function readyServerSnapshot(): boolean {
  return false;
}

export function update(next: TabState): void {
  write(next);
  cached = next;
  for (const listener of listeners) listener();
}

export function reset(): void {
  clear();
  // Dropping the cache rather than pinning it to `EMPTY_TAB` means the next
  // read comes from storage again, which is what a caller who cleared and then
  // wrote directly expects.
  cached = null;
  for (const listener of listeners) listener();
}
